import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { BackButton } from "@/components/ui/back-button";
import { useModalVisualViewport } from "@/hooks/useModalVisualViewport";

type ModalSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
  "2xl": "sm:max-w-2xl",
  "3xl": "sm:max-w-3xl",
};

export interface ModalShellProps {
  open: boolean;
  onClose?: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Accessible metadata when the visible heading lives in the custom body. */
  accessibleTitle?: React.ReactNode;
  accessibleDescription?: React.ReactNode;
  /** Visual alignment of the title within the header. Default `center`. */
  titleAlign?: "left" | "center";
  /** Render a back arrow on the left side of the header. */
  showBack?: boolean;
  onBack?: () => void;
  /** Show the circular X close button on the right of the header. Default true. */
  showClose?: boolean;
  /** Replace the entire header (icons + title) with custom content. */
  customHeader?: React.ReactNode;
  /** Extra content rendered after the title (e.g. tabs). Sits inside the header divider. */
  headerExtra?: React.ReactNode;
  /** Sticky footer rendered below the scrollable body. */
  footer?: React.ReactNode;
  /** Close when the overlay is clicked. Default true. */
  closeOnBackdrop?: boolean;
  /** Close when Escape is pressed. Default true. */
  closeOnEscape?: boolean;
  /** Hide the header divider line. Default false. */
  hideHeaderDivider?: boolean;
  /** Container max width on desktop. Default `lg`. */
  size?: ModalSize;
  /** Used for nested modals (e.g. profile completion over auth). Defaults to 50/60. */
  zIndex?: number;
  className?: string;
  contentClassName?: string;
  bodyClassName?: string;
  /** When false, the body is rendered without padding so callers can lay out edge-to-edge content. */
  padBody?: boolean;
  /** Keep an input sheet inside the mobile visible viewport when the keyboard pans/resizes it. */
  mobileVisualViewport?: boolean;
  /** Custom launchers outside Dialog.Trigger can explicitly restore focus. */
  onCloseAutoFocus?: (event: Event) => void;
  children: React.ReactNode;
  /** Optional test id stamped on the dialog content for e2e tests. */
  "data-testid"?: string;
}

/**
 * Airbnb-style modal shell — shared chrome used across the homepage.
 *
 * Anatomy:
 * - Dark blurred backdrop
 * - White surface, `rounded-2xl` on desktop, `rounded-t-2xl` on mobile (bottom sheet)
 * - Header bar with optional back arrow (left), title (center/left), circular X close (right)
 * - Thin divider under the header
 * - Scrollable body
 * - Optional sticky footer
 *
 * Animations: smooth slide-up on mobile, fade + scale on desktop.
 */
export function ModalShell({
  open,
  onClose,
  title,
  description,
  accessibleTitle,
  accessibleDescription,
  titleAlign = "center",
  showBack = false,
  onBack,
  showClose = true,
  customHeader,
  headerExtra,
  footer,
  closeOnBackdrop = true,
  closeOnEscape = true,
  hideHeaderDivider = false,
  size = "lg",
  zIndex,
  className,
  contentClassName,
  bodyClassName,
  padBody = true,
  mobileVisualViewport = false,
  onCloseAutoFocus,
  children,
  ...rest
}: ModalShellProps) {
  const overlayZ = zIndex ?? 50;
  const contentZ = (zIndex ?? 50) + 1;
  const viewportStyle = useModalVisualViewport(open && mobileVisualViewport);

  const handleOpenChange = (next: boolean) => {
    if (!next) onClose?.();
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          style={{ zIndex: overlayZ }}
          className={cn(
            "fixed inset-0 material-scrim",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
            "duration-200",
          )}
        />
        <DialogPrimitive.Content
          data-testid={rest["data-testid"]}
          onCloseAutoFocus={onCloseAutoFocus}
          onEscapeKeyDown={(e) => {
            if (!closeOnEscape) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (!closeOnBackdrop) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (!closeOnBackdrop) e.preventDefault();
          }}
          style={{ zIndex: contentZ, ...viewportStyle }}
          className={cn(
            // Mobile: pinned to the bottom, full-width sheet
            "fixed inset-x-0 bottom-0 flex w-full flex-col bg-white shadow-2xl outline-none",
            "max-h-[92vh] rounded-t-2xl",
            // Desktop: centred card
            "sm:inset-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2",
            "sm:max-h-[90vh] sm:w-full sm:rounded-2xl",
            SIZE_CLASS[size],
            // Animations
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
            "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
            "sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0",
            "sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95",
            "duration-200",
            className,
            contentClassName,
          )}
        >
          {/* Hidden a11y title/description so Radix never warns when callers omit them */}
          {title ? null : (
            <DialogPrimitive.Title asChild>
              <span className="sr-only">{accessibleTitle ?? "Dialog"}</span>
            </DialogPrimitive.Title>
          )}
          {description ? null : (
            <DialogPrimitive.Description className="sr-only">{accessibleDescription ?? "Dialog content"}</DialogPrimitive.Description>
          )}

          {customHeader ? (
            customHeader
          ) : (
            <div
              className={cn(
                "relative flex shrink-0 items-center gap-2 px-4 py-3 sm:px-6 sm:py-4",
                !hideHeaderDivider && "border-b border-gray-200",
              )}
            >
              <div className="flex w-10 shrink-0 items-center justify-start">
                {showBack ? (
                  <BackButton
                    variant="icon"
                    size="icon-sm"
                    icon="arrow"
                    iconClassName="h-5 w-5"
                    onClick={onBack ?? onClose}
                    aria-label="Back"
                    className="-ml-2"
                  />
                ) : null}
              </div>

              <div
                className={cn(
                  "flex min-w-0 flex-1 flex-col",
                  titleAlign === "center" ? "items-center text-center" : "items-start text-left",
                )}
              >
                {title ? (
                  <DialogPrimitive.Title className="truncate text-[15px] font-semibold leading-tight text-gray-900 sm:text-base">
                    {title}
                  </DialogPrimitive.Title>
                ) : null}
                {description ? (
                  <DialogPrimitive.Description className="mt-0.5 truncate text-xs text-gray-500">
                    {description}
                  </DialogPrimitive.Description>
                ) : null}
              </div>

              <div className="flex w-10 shrink-0 items-center justify-end">
                {showClose ? (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="cursor-pointer -mr-2 inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-900 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                  >
                    <X className="h-5 w-5" strokeWidth={2} />
                  </button>
                ) : null}
              </div>
            </div>
          )}

          {headerExtra ? <div className="shrink-0">{headerExtra}</div> : null}

          <div
            className={cn(
              "min-h-0 flex-1 overflow-y-auto",
              padBody && "px-5 py-5 sm:px-6 sm:py-6",
              bodyClassName,
            )}
          >
            {children}
          </div>

          {footer ? (
            <div className="shrink-0 border-t border-gray-200 bg-white px-5 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-6 sm:py-4">
              {footer}
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export default ModalShell;
